import { Box, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useInput } from '../../../../../customHook/useInput';
import { useImagePreview } from '../../../../../customHook/useProfilePicture';
import zeplinColor from '../../../../../theme/Color';
import CustomCard from '../../../../shared/CustomCard/CustomCard';
import CustomModal from '../../../../shared/CustomModal/CustomModal';
import CustomTextField from '../../../../shared/CustomTextField/CustomTextField';
import CustomButton from '../../../../shared/CustomButton/CustomButton';

interface AddIntegrationModalProps {
  handleClose: () => void;
  isModalOpen: boolean;
  onAdd: (integrationName: string, iconFile: string) => void;
  loading?: boolean;
  mode?: 'add' | 'edit';
  initialData?: {
    name?: string;
    iconUrl?: string;
    integrationId?: string;
  };
}

const AddIntegrationModal = ({
  handleClose,
  isModalOpen,
  onAdd,
  loading,
  mode = 'add',
  initialData,
}: AddIntegrationModalProps) => {
  const {
    handleChange: handleNameChange,
    handleBlur: handleNameBlur,
    value: integrationName,
    setValue: setIntegrationName,
  } = useInput(initialData?.name ?? '', () => true);

  const {
    fileInputRef,
    previewUrl,
    handleFileChange,
    clearSelection,
    openFileSelection,
    selectedFile,
    uploadProfilePicture,
  } = useImagePreview(initialData?.iconUrl ?? null);

  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!isModalOpen) {
      setIntegrationName('');
      setIsSaving(false);
      clearSelection();
    }
  }, [isModalOpen, clearSelection]);

  useEffect(() => {
    if (mode === 'edit' && initialData?.name) {
      setIntegrationName(initialData.name);
    }
  }, [mode, initialData, setIntegrationName]);

  const onSave = async () => {
    if (!integrationName.trim() || !selectedFile) {
      return;
    }

    setIsSaving(true);

    let uploadedUrl: string | null = null;
    if (previewUrl && previewUrl !== initialData?.iconUrl) {
      const result = await uploadProfilePicture({ key: false });
      uploadedUrl = result === undefined ? null : result;
    } else if (!previewUrl && initialData?.iconUrl && mode === 'edit') {
      // Keep existing image if no new image selected
      uploadedUrl = initialData.iconUrl;
    }
    if (uploadedUrl === null) {
      return;
    }
    onAdd(integrationName, uploadedUrl);
  };

  const handleModalClose = () => {
    setIntegrationName('');
    setIsSaving(false);
    clearSelection();
    handleClose();
  };

  return (
    <>
      <CustomModal handleClose={handleModalClose} open={isModalOpen}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
          }}
        >
          <CustomCard
            styles={{
              borderRadius: '14px',
              padding: { xs: '20px', xl: '24px' },
              backgroundColor: zeplinColor.Neutral1,
              width: '397px',
              boxShadow: 'rgba(0, 0, 0, 0.15)',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: { xs: '22px', xl: '32px' },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                }}
              >
                <Typography variant='heading3' color={zeplinColor.BasicBlack}>
                  {mode === 'edit' ? 'Edit Integration' : 'New Integration'}
                </Typography>
                <img
                  src={'/assets/close-icon.svg'}
                  alt='close'
                  style={{ cursor: 'pointer', width: '24px', height: '24px' }}
                  onClick={handleModalClose}
                />
              </Box>

              {/* Icon Upload Section */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  gap: '16px',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '80px',
                    height: '80px',
                    borderRadius: '22px',
                    border: !previewUrl ? `1px dashed ${zeplinColor.Neutral9}` : '',
                    overflow: 'hidden',
                  }}
                >
                  <img
                    src={previewUrl ?? '/assets/integration-icon-placeholder.svg'}
                    alt='integration icon'
                    width={previewUrl ? 80 : 35}
                    height={previewUrl ? 80 : 35}
                    style={{ objectFit: 'cover' }}
                  />
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    gap: '12px',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Typography variant='body2' sx={{ color: zeplinColor.BasicBlack }}>
                      Integration Icon
                    </Typography>
                    <Typography variant='label1' sx={{ color: zeplinColor.Neutral8 }}>
                      JPG or PNG (max 2 MB)
                    </Typography>
                  </Box>
                  <Box>
                    <CustomButton
                      disabled={false}
                      onClick={openFileSelection}
                      variant='contained'
                      color={'primary'}
                      buttonVariant='buttonSmall'
                      style={{
                        borderRadius: { xs: '10px', xl: '14px' },
                        padding: '6px 12px',
                        '&.Mui-focusVisible': { backgroundColor: '#333CAA' },
                      }}
                      textStyle={{ color: zeplinColor.Neutral1 }}
                      text={previewUrl ? 'Change Icon' : 'Add Icon'}
                    />
                    <input
                      type='file'
                      multiple
                      accept='.jpeg, .jpg, .png, image/jpeg, image/png'
                      ref={fileInputRef}
                      style={{ display: 'none' }}
                      onChange={handleFileChange}
                    />
                    {previewUrl && (
                      <CustomButton
                        disabled={false}
                        variant='contained'
                        buttonVariant='buttonSmall'
                        onClick={clearSelection}
                        text={'Delete'}
                        textStyle={{
                          color: zeplinColor.Error,
                        }}
                        style={{
                          backgroundColor: 'transparent',
                          borderRadius: { xs: '10px', xl: '14px' },
                          marginLeft: '8px',
                          padding: '6px 12px',
                          '&.Mui-focusVisible, &:hover': { backgroundColor: zeplinColor.Neutral3 },
                        }}
                      />
                    )}
                  </Box>
                </Box>
              </Box>

              <CustomTextField
                style={{ width: '100%' }}
                onBlur={handleNameBlur}
                onChange={handleNameChange}
                name='Integration Name'
                text={integrationName}
                form={true}
                error={false}
                label={'Integration Name'}
                type='text'
                tabindex={1}
              />
              <Box
                sx={{
                  display: 'flex',
                  gap: { xs: '10px', xl: '16px' },
                }}
              >
                <Box
                  onClick={handleModalClose}
                  sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}
                >
                  <CustomCard
                    styles={{
                      borderRadius: '14px',
                      padding: '14px 32px',
                      backgroundColor: zeplinColor.Neutral1,
                      border: `1px solid ${zeplinColor.BasicBlack}`,
                      cursor: 'pointer',
                      display: 'flex',
                      justifyContent: 'center',
                      flex: 1,
                    }}
                    aria-label='button'
                  >
                    <Typography variant='buttonMedium' color={zeplinColor.BasicBlack}>
                      Cancel
                    </Typography>
                  </CustomCard>
                </Box>
                <Box
                  onClick={() => {
                    onSave();
                  }}
                  sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}
                >
                  <CustomCard
                    styles={{
                      borderRadius: '14px',
                      padding: '14px 32px',
                      backgroundColor:
                        !previewUrl || isSaving || loading || !integrationName.trim()
                          ? zeplinColor.Neutral4
                          : zeplinColor.Brand1,
                      cursor:
                        !previewUrl || isSaving || loading || !integrationName.trim()
                          ? 'not-allowed'
                          : 'pointer',
                      display: 'flex',
                      justifyContent: 'center',
                      flex: 1,
                    }}
                    aria-label='button'
                    aria-disabled={!previewUrl || isSaving || loading || !integrationName.trim()}
                  >
                    <Typography
                      variant='buttonMedium'
                      color={
                        !previewUrl || isSaving || loading || !integrationName.trim()
                          ? zeplinColor.BasicBlack
                          : zeplinColor.Neutral1
                      }
                      sx={{ whiteSpace: 'nowrap', textAlign: 'center' }}
                    >
                      {loading || isSaving ? 'Saving...' : 'Save'}
                    </Typography>
                  </CustomCard>
                </Box>
              </Box>
            </Box>
          </CustomCard>
        </Box>
      </CustomModal>
    </>
  );
};

export default AddIntegrationModal;
